import { t } from '@lingui/macro'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { ContentProgressPageLayout } from '@/layout/components/content-progress-page-layout/ContentProgressPageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { usePatientCommunication } from '@/modules/care-question/hooks/usePatientCommunication'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useCareQuestionContext } from '../../context/CareQuestion.context'

import type { AppHeaderProps } from '@/@bothrs-ui/web/components/navigation/header/AppHeader.types'
import type { ContentProgressPageLayoutHandle } from '@/layout/components/content-progress-page-layout/ContentProgressPageLayout'
import type { ESignDoctor } from '@/modules/appointment/types/e-sign.types'
import type { StepData } from '../../context/CareQuestion.context'
import type { PatientCommunicationSpecialisation } from '../../types/patient-communication.type'

export type Props = {
  title: string
  category: string
  receiverType: 'doctor' | 'patient' | 'hospital'
  steps: {
    hideProgressBar?: boolean
    content: (
      onNext: (data?: Record<string, unknown>) => void,
      onPrevious: () => void,
      header?: AppHeaderProps,
      progress?: { min: number; max: number; value: number },
    ) => JSX.Element
  }[]
  submittedScreen: JSX.Element
}

export const PatientCommunicationFlow = ({ title, category, receiverType, steps, submittedScreen }: Props) => {
  // State
  // ------------------------------------------------------------------------- /

  const [index, setIndex] = useState(0)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const { questionData, setQuestionData } = useCareQuestionContext()

  // Hooks
  // ------------------------------------------------------------------------- /

  const navigate = useNavigate()
  const pageLayoutRef = useRef<ContentProgressPageLayoutHandle>()
  const { showErrorToast } = useToast()

  const {
    createPatientCommunication: {
      execute: createPatientCommunication,
      value: isSubmissionSuccessful,
      error: createCareplanActivityError,
    },
  } = usePatientCommunication()

  // Hide navigation on native devices
  useHideNativeNavigation()

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (createCareplanActivityError) {
      mixpanel.track('patient_communication_submission_error', {
        error: createCareplanActivityError,
      })

      showErrorToast({
        message: t({
          id: 'patient-communication.error',
          message: 'Er is een fout opgetreden. Probeer het later opnieuw.',
        }),
      })
    }
  }, [createCareplanActivityError, showErrorToast])

  useEffect(() => {
    if (hasSubmitted) {
      mixpanel.track('patient_communication_submitted')
      setHasSubmitted(true)
    }
  }, [hasSubmitted])

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  const handleNext = (data?: Record<string, unknown>) => {
    if (data) {
      setQuestionData((previous) => ({ ...previous, ...data }))
    }

    if (index === steps.length - 1) {
      handleSubmit({ ...questionData, ...data })
    } else {
      pageLayoutRef.current?.handleNext()
    }
  }

  const handlePrevious = () => {
    pageLayoutRef.current?.handlePrevious()
  }

  const handleSubmit = (data: StepData) => {
    if (!data) return

    const { message, specialisation, doctor } = data
    if (!message) {
      return
    }

    createPatientCommunication(
      category,
      message as string,
      title,
      receiverType,
      specialisation as PatientCommunicationSpecialisation,
      doctor as ESignDoctor,
    )
  }

  const handleClose = () => {
    mixpanel.track('patient_communication_flow_closed')
    navigate('/', { replace: true })
  }

  // Render
  // ------------------------------------------------------------------------- /
  const progress = { min: 0, max: steps.length, value: index + 1 }

  return isSubmissionSuccessful ? (
    submittedScreen
  ) : (
    <ContentProgressPageLayout
      headerPrimaryAction={{
        icon: ['far', 'chevron-left'],
        label: t({
          id: 'globals.previous-step',
          comment: 'Global for previous step',
          message: 'Vorige stap',
        }),
      }}
      headerSecondaryAction={{
        icon: ['far', 'close'],
        label: t({
          id: 'globals.close',
        }),
      }}
      index={index + 1}
      hideProgressBar={steps[index]?.hideProgressBar}
      onUpdateIndex={(newIndex) => setIndex(newIndex - 1)}
      onGoBack={handleClose}
      maxIndex={steps.length}
      minIndex={0}
      ref={pageLayoutRef}
    >
      <StContainer>{steps[index]?.content(handleNext, handlePrevious, undefined, progress)}</StContainer>
    </ContentProgressPageLayout>
  )
}

const StContainer = styled.div`
  height: 100%;
  width: 100%;
`
